import { Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronDown,
  CloudRain,
  FileDown,
  Loader2,
  SlidersHorizontal,
  Truck,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FieldOpsFilterCheckboxRow,
  FieldOpsFilterRow,
} from "@/components/field-ops/field-ops-filters";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { type DailyLog, useCreateDailyLog, useDailyLogs } from "@/hooks/use-daily-logs";
import { useProjects } from "@/hooks/use-projects";
import { useSession } from "@/lib/auth-client";
import { cn, formatDateOnlyLocal, localDateISOString, localDateMinusDaysISO } from "@/lib/utils";

type DailyLogStatus = DailyLog["status"];

function StatusBadge({ status }: { status: DailyLogStatus }) {
  const map: Record<
    DailyLogStatus,
    { label: string; variant: "default" | "secondary" | "outline" | "destructive" }
  > = {
    draft: { label: "Draft", variant: "outline" },
    submitted: { label: "Submitted", variant: "default" },
    signed_off: { label: "Signed Off", variant: "default" },
    void: { label: "Void", variant: "secondary" },
  };
  const cfg = map[status];
  return (
    <Badge
      variant={cfg.variant}
      className={
        status === "submitted"
          ? "bg-yellow-100 text-yellow-800 border-yellow-300"
          : status === "signed_off"
            ? "bg-green-100 text-green-800 border-green-300"
            : undefined
      }
    >
      {cfg.label}
    </Badge>
  );
}

function formatDate(date: string | null): string {
  return formatDateOnlyLocal(date, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface NewDailyLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function NewDailyLogDialog({ open, onOpenChange }: NewDailyLogDialogProps) {
  const navigate = useNavigate();
  const today = localDateISOString();
  const [date, setDate] = useState(today);
  const [backdateReason, setBackdateReason] = useState("");
  const [projectId, setProjectId] = useState("");

  const { data: projects, isLoading: projectsLoading } = useProjects();
  const createMutation = useCreateDailyLog();
  const isBackdate = date < today;

  useEffect(() => {
    if (open) {
      setProjectId("");
      setDate(localDateISOString());
      setBackdateReason("");
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    const result = await createMutation.mutateAsync({
      projectId,
      date,
      backdateReason: isBackdate ? backdateReason : undefined,
    });
    onOpenChange(false);
    setDate(localDateISOString());
    setBackdateReason("");
    setProjectId("");
    void navigate({ to: "/daily-logs/$id", params: { id: result.id } });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>New Daily Log</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="dl-project">Project</Label>
            <Select
              value={projectId || undefined}
              onValueChange={setProjectId}
              disabled={projectsLoading}
            >
              <SelectTrigger id="dl-project" className="min-h-[44px] w-full">
                <SelectValue
                  placeholder={projectsLoading ? "Loading projects…" : "Select project"}
                />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4}>
                {(projects ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dl-date">Date</Label>
            <input
              id="dl-date"
              type="date"
              required
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={date}
              min={localDateMinusDaysISO(7)}
              max={today}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {isBackdate && (
            <div className="space-y-1.5">
              <Label htmlFor="dl-reason">Backdate Reason</Label>
              <Textarea
                id="dl-reason"
                placeholder="Why is this log being backdated?"
                required
                value={backdateReason}
                onChange={(e) => setBackdateReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || !projectId}>
              {createMutation.isPending ? "Creating…" : "Create Log"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DailyLogCardProps {
  log: DailyLog;
  projectName?: string;
}

function DailyLogCard({ log, projectName }: DailyLogCardProps) {
  const navigate = useNavigate();

  return (
    <Card
      className="cursor-pointer gap-0 py-0 shadow-sm hover:shadow-md transition-shadow active:scale-[0.99]"
      onClick={() => void navigate({ to: "/daily-logs/$id", params: { id: log.id } })}
    >
      <CardContent className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{formatDate(log.date)}</span>
            <StatusBadge status={log.status} />
            {log.hasWeatherDelay && (
              <span title="Weather delay flagged">
                <CloudRain className="h-4 w-4 text-blue-500" />
              </span>
            )}
          </div>
          {projectName ? (
            <span className="text-xs text-muted-foreground">{projectName}</span>
          ) : null}
          {log.submittedByName && (
            <span className="text-xs text-muted-foreground">
              Submitted by {log.submittedByName}
            </span>
          )}
          {!log.submittedByName && log.createdBy && (
            <span className="text-xs text-muted-foreground">Created by {log.createdBy}</span>
          )}
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {log.manpowerCount !== null && (
            <span className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {log.manpowerCount} workers
            </span>
          )}
          {log.deliveryCount !== null && (
            <span className="flex items-center gap-1">
              <Truck className="h-3.5 w-3.5" />
              {log.deliveryCount} deliveries
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface DailyLogsDesktopTableProps {
  logs: DailyLog[];
  projectNameById: Map<string, string>;
}

function DailyLogsDesktopTableRow({ log, projectName }: { log: DailyLog; projectName?: string }) {
  const submitter = log.submittedByName ?? log.createdBy;

  return (
    <TableRow className="hover:bg-muted/50">
      <TableCell className="py-2.5 whitespace-nowrap">
        <Link to="/daily-logs/$id" params={{ id: log.id }} className="font-medium hover:underline">
          {formatDate(log.date)}
        </Link>
      </TableCell>
      <TableCell className="py-2.5 max-w-[200px] truncate text-muted-foreground">
        {projectName ?? "—"}
      </TableCell>
      <TableCell className="py-2.5">
        <StatusBadge status={log.status} />
      </TableCell>
      <TableCell className="py-2.5">
        {log.hasWeatherDelay ? (
          <span className="inline-flex items-center gap-1 text-sm text-blue-600">
            <CloudRain className="h-4 w-4" />
            Delay
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="py-2.5 text-right tabular-nums text-muted-foreground">
        {log.manpowerCount !== null ? (
          <span className="inline-flex items-center justify-end gap-1">
            <Users className="h-3.5 w-3.5" />
            {log.manpowerCount}
          </span>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell className="py-2.5 text-right tabular-nums text-muted-foreground">
        {log.deliveryCount !== null ? (
          <span className="inline-flex items-center justify-end gap-1">
            <Truck className="h-3.5 w-3.5" />
            {log.deliveryCount}
          </span>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell className="py-2.5 max-w-[160px] truncate text-muted-foreground text-sm">
        {submitter ?? "—"}
      </TableCell>
    </TableRow>
  );
}

function DailyLogsDesktopTable({ logs, projectNameById }: DailyLogsDesktopTableProps) {
  const sorted = [...logs].sort((a, b) => {
    const da = a.date ?? "";
    const db = b.date ?? "";
    return db.localeCompare(da);
  });

  return (
    <Card className="gap-0 py-0 shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Date</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Weather</TableHead>
            <TableHead className="text-right">Manpower</TableHead>
            <TableHead className="text-right">Deliveries</TableHead>
            <TableHead>Submitted by</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((log) => (
            <DailyLogsDesktopTableRow
              key={log.id}
              log={log}
              projectName={projectNameById.get(log.projectId)}
            />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

type DailyLogsFilterProps = {
  filterProjectId: string;
  setFilterProjectId: (v: string) => void;
  dateFrom: string;
  setDateFrom: (v: string) => void;
  dateTo: string;
  setDateTo: (v: string) => void;
  weatherDelayOnly: boolean;
  setWeatherDelayOnly: (v: boolean) => void;
  sortedProjects: Array<{ id: string; name: string }>;
};

function formatFilterDate(iso: string): string {
  if (!iso) return "";
  return formatDateOnlyLocal(iso, { month: "short", day: "numeric", year: "numeric" });
}

function buildDailyLogActiveFilterLabels(props: DailyLogsFilterProps): string[] {
  const labels: string[] = [];
  if (props.filterProjectId) {
    const project = props.sortedProjects.find((p) => p.id === props.filterProjectId);
    labels.push(project?.name ?? "Project");
  }
  if (props.dateFrom) labels.push(`From ${formatFilterDate(props.dateFrom)}`);
  if (props.dateTo) labels.push(`To ${formatFilterDate(props.dateTo)}`);
  if (props.weatherDelayOnly) labels.push("Weather delays");
  return labels;
}

function DailyLogsFilterFields({
  idPrefix,
  ...props
}: DailyLogsFilterProps & { idPrefix: string }) {
  const triggerClass = "min-h-[44px] w-full";
  const inputClass = "min-h-[44px]";

  return (
    <>
      <FieldOpsFilterRow label="Project" htmlFor={`${idPrefix}-project`}>
        <Select
          value={props.filterProjectId || "all"}
          onValueChange={(v) => props.setFilterProjectId(v === "all" ? "" : v)}
        >
          <SelectTrigger id={`${idPrefix}-project`} className={triggerClass}>
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent position="popper" sideOffset={4}>
            <SelectItem value="all">All projects</SelectItem>
            {props.sortedProjects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldOpsFilterRow>

      <FieldOpsFilterRow label="From" htmlFor={`${idPrefix}-from`}>
        <Input
          id={`${idPrefix}-from`}
          type="date"
          value={props.dateFrom}
          onChange={(e) => props.setDateFrom(e.target.value)}
          className={inputClass}
        />
      </FieldOpsFilterRow>

      <FieldOpsFilterRow label="To" htmlFor={`${idPrefix}-to`}>
        <Input
          id={`${idPrefix}-to`}
          type="date"
          value={props.dateTo}
          onChange={(e) => props.setDateTo(e.target.value)}
          className={inputClass}
        />
      </FieldOpsFilterRow>

      <FieldOpsFilterCheckboxRow
        id={`${idPrefix}-weather`}
        checked={props.weatherDelayOnly}
        onCheckedChange={props.setWeatherDelayOnly}
        labelText="Weather delays only"
      />
    </>
  );
}

/** Collapsed-by-default filters on mobile so log cards stay above the fold. */
function DailyLogsMobileFilters(props: DailyLogsFilterProps) {
  const [open, setOpen] = useState(false);
  const activeLabels = useMemo(() => buildDailyLogActiveFilterLabels(props), [props]);
  const activeCount = activeLabels.length;

  function clearFilters() {
    props.setFilterProjectId("");
    props.setDateFrom("");
    props.setDateTo("");
    props.setWeatherDelayOnly(false);
  }

  return (
    <Card className="gap-0 py-0 shadow-sm overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-3 min-h-[44px] text-left sm:px-4"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="dl-mobile-filters-panel"
      >
        <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Filters</span>
            {activeCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeCount}
              </Badge>
            )}
          </div>
          {!open && activeCount > 0 && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {activeLabels.join(" · ")}
            </p>
          )}
          {!open && activeCount === 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">Tap to refine the list</p>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <CardContent
          id="dl-mobile-filters-panel"
          className="space-y-2 border-t px-3 py-2 sm:px-4 sm:py-2.5"
        >
          <DailyLogsFilterFields idPrefix="dl-filter" {...props} />
          {activeCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full min-h-[44px] text-muted-foreground"
              onClick={clearFilters}
            >
              Clear all filters
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function DailyLogsDesktopFilters(props: DailyLogsFilterProps) {
  const activeCount = buildDailyLogActiveFilterLabels(props).length;

  function clearFilters() {
    props.setFilterProjectId("");
    props.setDateFrom("");
    props.setDateTo("");
    props.setWeatherDelayOnly(false);
  }

  return (
    <Card className="gap-0 py-0 shadow-sm">
      <CardContent className="px-4 py-3">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="dl-dt-project" className="text-xs text-muted-foreground">
              Project
            </Label>
            <Select
              value={props.filterProjectId || "all"}
              onValueChange={(v) => props.setFilterProjectId(v === "all" ? "" : v)}
            >
              <SelectTrigger id="dl-dt-project" className="h-9 w-full">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4}>
                <SelectItem value="all">All projects</SelectItem>
                {props.sortedProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="dl-dt-from" className="text-xs text-muted-foreground">
              From
            </Label>
            <Input
              id="dl-dt-from"
              type="date"
              value={props.dateFrom}
              onChange={(e) => props.setDateFrom(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="dl-dt-to" className="text-xs text-muted-foreground">
              To
            </Label>
            <Input
              id="dl-dt-to"
              type="date"
              value={props.dateTo}
              onChange={(e) => props.setDateTo(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="flex items-end gap-3">
            <div className="flex items-center gap-2 h-9">
              <Checkbox
                id="dl-dt-weather"
                checked={props.weatherDelayOnly}
                onCheckedChange={(v) => props.setWeatherDelayOnly(v === true)}
              />
              <Label htmlFor="dl-dt-weather" className="text-sm font-normal cursor-pointer">
                Weather delays only
              </Label>
            </div>
            {activeCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 shrink-0"
                onClick={clearFilters}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

async function downloadDailyLogsRangePdf(
  projectId: string,
  dateFrom: string,
  dateTo: string,
): Promise<void> {
  const params = new URLSearchParams({ projectId, dateFrom, dateTo });
  const res = await fetch(`/api/dailylogs/export?${params}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `Export failed (${res.status})` }));
    throw new Error((err as { error?: string }).error ?? `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disposition = res.headers.get("Content-Disposition");
  const match = disposition?.match(/filename="([^"]+)"/);
  a.download = match?.[1] ?? `daily-logs-${dateFrom}-${dateTo}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export function DailyLogsPage() {
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [weatherDelayOnly, setWeatherDelayOnly] = useState(false);
  const [filterProjectId, setFilterProjectId] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const { data: session } = useSession();
  const { data: projects = [] } = useProjects();
  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects],
  );

  const listFilters = useMemo(() => {
    const f: {
      projectId?: string;
      dateFrom?: string;
      dateTo?: string;
      hasWeatherDelay?: boolean;
    } = {};
    if (filterProjectId) f.projectId = filterProjectId;
    if (dateFrom) f.dateFrom = dateFrom;
    if (dateTo) f.dateTo = dateTo;
    if (weatherDelayOnly) f.hasWeatherDelay = true;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [filterProjectId, dateFrom, dateTo, weatherDelayOnly]);

  const { data, isLoading, isError, error, refetch } = useDailyLogs(listFilters);

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, p.name);
    return m;
  }, [projects]);

  const logs = data ?? [];
  const canExportPdf = session?.user?.role !== "contractor";
  const canExportRange =
    canExportPdf &&
    filterProjectId.length > 0 &&
    dateFrom.length > 0 &&
    dateTo.length > 0 &&
    dateFrom <= dateTo;

  return (
    <div className="space-y-4 lg:space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold">Daily Logs</h1>
        <div className="flex items-center gap-2">
          {canExportPdf && (
            <Button
              type="button"
              variant="outline"
              className="min-h-[44px] lg:min-h-9 gap-2"
              disabled={!canExportRange || pdfBusy}
              title={
                canExportRange
                  ? "Download combined PDF for selected project and date range"
                  : "Select project, from date, and to date to export"
              }
              onClick={() => {
                setPdfBusy(true);
                void downloadDailyLogsRangePdf(filterProjectId, dateFrom, dateTo)
                  .catch((e: unknown) => {
                    toast.error(e instanceof Error ? e.message : "Export failed");
                  })
                  .finally(() => setPdfBusy(false));
              }}
            >
              {pdfBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Export range (PDF)</span>
            </Button>
          )}
          <Button className="min-h-[44px] lg:min-h-9" onClick={() => setNewDialogOpen(true)}>
            + New Log
          </Button>
        </div>
      </div>

      <div className="hidden lg:block">
        <DailyLogsDesktopFilters
          filterProjectId={filterProjectId}
          setFilterProjectId={setFilterProjectId}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          weatherDelayOnly={weatherDelayOnly}
          setWeatherDelayOnly={setWeatherDelayOnly}
          sortedProjects={sortedProjects}
        />
      </div>

      <div className="lg:hidden">
        <DailyLogsMobileFilters
          filterProjectId={filterProjectId}
          setFilterProjectId={setFilterProjectId}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          weatherDelayOnly={weatherDelayOnly}
          setWeatherDelayOnly={setWeatherDelayOnly}
          sortedProjects={sortedProjects}
        />
      </div>

      {isLoading && (
        <>
          <div className="space-y-2 lg:hidden">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[4.5rem] w-full rounded-lg" />
            ))}
          </div>
          <div className="hidden lg:block">
            <Skeleton className="h-80 w-full rounded-lg" />
          </div>
        </>
      )}

      {isError && (
        <ErrorState
          message={error?.message ?? "Failed to load daily logs"}
          onRetry={() => void refetch()}
        />
      )}

      {!isLoading && !isError && logs.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <p className="mb-4">No daily logs found.</p>
            <Button variant="outline" onClick={() => setNewDialogOpen(true)}>
              + New Log
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && logs.length > 0 && (
        <>
          <div className="space-y-2 lg:hidden">
            {logs.map((log) => (
              <DailyLogCard
                key={log.id}
                log={log}
                projectName={projectNameById.get(log.projectId)}
              />
            ))}
          </div>
          <div className="hidden lg:block">
            <DailyLogsDesktopTable logs={logs} projectNameById={projectNameById} />
          </div>
        </>
      )}

      <NewDailyLogDialog open={newDialogOpen} onOpenChange={setNewDialogOpen} />
    </div>
  );
}
